// ── Account monitoring: UCC, tax, MCA, bank and news for ONE borrower ────────────────────
//
// The three registers each have a book-wide page (UCC Filings, Tax Monitoring,
// MCA Monitor). This is the same information scoped to the account in front of
// you, so a portfolio manager reviewing a credit does not have to leave it.
//
//   <MonitoringSummary>   three cards on the Overview tab — the headline only
//   <MonitoringTab>       the Monitoring tab — the per-account detail
//
// Reads the SAME endpoints as the register pages, filtered by accountid, so the
// account view and the register cannot disagree about a filing, a lien or a
// finding. MCA is bank activity, so it needs the bank-feed permission; without
// it the card says so instead of showing an empty "nothing found".
import { useState, useEffect, Fragment } from "react";
import { B } from "../../../shared/theme";
import { fmt, fmtDate } from "../../../shared/format";
import { Btn, Card, Icon, STh, Td, TdNum, Th, ThNum } from "../../../shared/ui";
import { useSortable } from "../../../shared/hooks";
import { useApp } from "../../../shared/appContext";
import { authState, isUnmonitored } from "../../../shared/taxCoverage";
import { MCAMonitorPage } from "../pages/MCAMonitorPage";
import { BankActivityPage } from "../pages/BankActivityPage";
import { CscDocLinks, FilingDocs } from "./CscDocLinks";
import { FilingDetail, FilingForm } from "../pages/UCCRegisterPage";
import { SearchTable, SearchDetail, currentOnly } from "../pages/UCCSearchesPage";
import { DecisionModal, SubjectModal } from "../pages/NewsMonitorPage";

const RED = { bg: "#FBE4DD", fg: "#993C1D" };
const AMBER = { bg: "#FDF0D5", fg: "#7A5200" };
const GREEN = { bg: "#E1F5EE", fg: "#0F6E56" };
const GREY = { bg: "#F1EFE8", fg: "#5F5E5A" };

const Pill = ({ tone, children, title }) => (
  <span title={title} style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 10, whiteSpace: "nowrap",
                               background: tone.bg, color: tone.fg, display: "inline-block" }}>{children}</span>
);
const cardTitle = { fontSize: 11, fontWeight: 600, color: B.cyanDark, textTransform: "uppercase", letterSpacing: "0.06em",
                    marginBottom: 12, paddingBottom: 6, borderBottom: "1px solid " + B.cyanLight,
                    display: "flex", alignItems: "center", gap: 8 };
const Row = ({ label, value }) => (
  <div style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 13, padding: "4px 0" }}>
    <span style={{ color: "var(--color-text-secondary)", flexShrink: 0 }}>{label}</span>
    <span style={{ fontWeight: 500, textAlign: "right" }}>{value}</span>
  </div>
);
const LinkBtn = ({ onClick, children }) => (
  <span onClick={onClick} style={{ marginLeft: "auto", fontSize: 11, fontWeight: 600, color: B.cyan, cursor: "pointer",
                                   textTransform: "none", letterSpacing: 0 }}>{children}</span>
);
const Note = ({ children }) => <div style={{ fontSize: 12, color: "var(--color-text-secondary)", padding: "6px 0" }}>{children}</div>;

// ── UCC: the same reading of a filing the register uses ──
const uccIssue = v => {
  if (v.status === "LAPSED") return { tone: RED, text: "Lapsed — unperfected" };
  if (v.jurisdiction_mismatch) return { tone: RED, text: `Filed in ${v.jurisdiction_state}, entity registered in ${v.debtorstate_now || "another state"}` };
  if (v.name_drift) return { tone: AMBER, text: "Debtor renamed since filing — amend within 4 months" };
  if (v.in_continuation_window) return { tone: AMBER, text: `Continuation window open — ${v.days_to_lapse} days to lapse` };
  return null;
};
const uccTone = v => uccIssue(v)?.tone
  || (v.status === "Terminated" || v.status === "No lapse" ? GREY : GREEN);
const isLive = v => v.status !== "Terminated";

// ── Data: all three, in parallel, each allowed to fail on its own ──
export const useAccountMonitoring = (accountid) => {
  const { api, role } = useApp();
  const canMCA = !!role.canViewBankFeeds;
  const [d, setD] = useState({ loading: true });
  const [tick, setTick] = useState(0);      // bumped to reload after an edit
  useEffect(() => {
    if (!accountid) return;
    let live = true;
    if (!tick) setD({ loading: true });
    Promise.allSettled([
      // events: the filing's history, for the detail window opened from a row.
      api.get("getUCCFilings", { accountid, events: 1 }),
      api.get("getTaxMonitoring", { accountid }),
      canMCA ? api.get("getMCAFindings", { accountid, status: "all" }) : Promise.resolve(null),
      canMCA ? api.get("getBankAlerts", { accountid, status: "all" }) : Promise.resolve(null),
      api.get("getUCCSearches", { accountid }),
    ]).then(([u, t, m, b, s]) => {
      if (!live) return;
      setD({
        loading: false,
        ucc: u.status === "fulfilled" ? (u.value && u.value.rows) || [] : null,
        uccEvents: u.status === "fulfilled" ? (u.value && u.value.events) || [] : [],
        // CSC searches matched to this borrower; none when CSC is not set up.
        searches: s.status === "fulfilled" ? (s.value && s.value.rows) || [] : [],
        uccErr: u.status === "rejected" ? u.reason.message : null,
        tax: t.status === "fulfilled" ? t.value || {} : null,
        taxErr: t.status === "rejected" ? t.reason.message : null,
        mca: canMCA && m.status === "fulfilled" ? (m.value && m.value.findings) || [] : null,
        mcaErr: canMCA && m.status === "rejected" ? m.reason.message : null,
        bank: canMCA && b.status === "fulfilled" ? (b.value && b.value.alerts) || [] : null,
        bankErr: canMCA && b.status === "rejected" ? b.reason.message : null,
        canMCA,
      });
    });
    return () => { live = false; };
  }, [accountid, canMCA, tick]); // eslint-disable-line react-hooks/exhaustive-deps
  return { ...d, reload: () => setTick(t => t + 1) };
};

const taxFacts = tax => {
  const liabilities = (tax && tax.liabilities) || [];
  const worklist = (tax && tax.worklist) || [];
  const entities = (tax && tax.entities) || [];
  const open = liabilities.filter(l => !l.released && (l.amount == null || l.amount > 0));
  return {
    entities, worklist, liabilities,
    openWork: worklist.filter(w => !w.acked_at),
    unpaid: open,
    unpaidAmt: open.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0),
    liens: liabilities.filter(l => l.lien_filed && !l.released),
    atRisk: liabilities.filter(l => l.advancing_at_risk),
    monitored: entities.filter(e => authState(e).key === "ok" || authState(e).key === "expiring"),
    unmonitored: entities.filter(isUnmonitored),
  };
};

// ═════════════════════════════ OVERVIEW CARDS ═════════════════════════════
export const MonitoringSummary = ({ accountid, onOpen }) => {
  const d = useAccountMonitoring(accountid);
  const Panel = ({ title, status, children }) => (
    <Card style={{ padding: 16 }}>
      <div style={cardTitle}>{title}{status}<LinkBtn onClick={onOpen}>Details →</LinkBtn></div>
      {d.loading ? <Note>Loading…</Note> : children}
    </Card>
  );

  // UCC
  const ucc = d.ucc || [];
  const live = ucc.filter(isLive);
  const uccIssues = live.map(v => ({ v, i: uccIssue(v) })).filter(x => x.i);
  const nextLapse = live.filter(v => v.lapsedate).sort((a, b) => String(a.lapsedate).localeCompare(String(b.lapsedate)))[0];
  const uccStatus = d.loading || !d.ucc ? null
    : uccIssues.some(x => x.i.tone === RED) ? <Pill tone={RED}>{uccIssues.length} need attention</Pill>
    : uccIssues.length ? <Pill tone={AMBER}>{uccIssues.length} need attention</Pill>
    : live.length ? <Pill tone={GREEN}>In order</Pill> : <Pill tone={AMBER}>No filing</Pill>;

  // Tax
  const t = taxFacts(d.tax);
  const taxStatus = d.loading || !d.tax ? null
    : t.atRisk.length ? <Pill tone={RED}>Advances at risk</Pill>
    : t.openWork.length ? <Pill tone={AMBER}>{t.openWork.length} open</Pill>
    : t.unmonitored.length ? <Pill tone={AMBER}>Coverage gap</Pill>
    : <Pill tone={GREEN}>Clear</Pill>;

  // MCA
  const mca = d.mca || [];
  const mOpen = mca.filter(f => f.status === "Open");
  const mAck = mca.filter(f => f.status === "Acknowledged");
  const mNew = mca.reduce((s, f) => s + (f.newsincedecision || 0), 0);
  const mLast = mca.map(f => f.lastdate).filter(Boolean).sort().pop();
  const mcaStatus = d.loading || !d.canMCA || !d.mca ? null
    : mOpen.some(f => f.confidence === "High") ? <Pill tone={RED}>{mOpen.length} open</Pill>
    : mOpen.length ? <Pill tone={AMBER}>{mOpen.length} open</Pill>
    : mAck.length ? <Pill tone={AMBER}>Known MCA</Pill>
    : <Pill tone={GREEN}>None found</Pill>;

  // Bank activity
  const bank = d.bank || [];
  const bOpen = bank.filter(a => a.status === "Open");
  const bAck = bank.filter(a => a.status === "Acknowledged");
  const bankStatus = d.loading || !d.canMCA || !d.bank ? null
    : bOpen.some(a => a.severity === "High") ? <Pill tone={RED}>{bOpen.length} open</Pill>
    : bOpen.length ? <Pill tone={AMBER}>{bOpen.length} open</Pill>
    : <Pill tone={GREEN}>Clear</Pill>;

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12, alignItems: "start", marginTop: 12 }}>
      <Panel title="UCC Filings" status={uccStatus}>
        {d.uccErr ? <Note>{d.uccErr}</Note> : <>
          <Row label="Live filings" value={live.length} />
          <Row label="Next lapse" value={nextLapse ? `${fmtDate(nextLapse.lapsedate)} (${nextLapse.days_to_lapse}d)` : "—"} />
          {uccIssues.slice(0, 2).map(({ v, i }) => (
            <div key={v.uccfilingid} style={{ fontSize: 12, color: i.tone.fg, padding: "3px 0" }}>
              {v.jurisdiction_state} {v.filingnumber || ""} — {i.text}</div>
          ))}
          {!live.length && <Note>No financing statement recorded for this borrower.</Note>}
        </>}
      </Panel>

      <Panel title="Tax Monitoring" status={taxStatus}>
        {d.taxErr ? <Note>{d.taxErr}</Note> : <>
          <Row label="Entities monitored" value={`${t.monitored.length} of ${t.entities.length}`} />
          <Row label="Open findings" value={t.openWork.length} />
          <Row label="Unpaid liabilities" value={t.unpaid.length ? `${t.unpaid.length} · ${fmt(t.unpaidAmt)}` : "0"} />
          <Row label="Liens filed" value={t.liens.length} />
        </>}
      </Panel>

      <Panel title="MCA Activity" status={mcaStatus}>
        {!d.canMCA ? <Note>Needs bank-feed access (portfolio managers and admins).</Note>
          : d.mcaErr ? <Note>{d.mcaErr}</Note> : <>
          <Row label="Open findings" value={mOpen.length ? `${mOpen.length} (${mOpen.filter(f => f.confidence === "High").length} high)` : "0"} />
          <Row label="Acknowledged" value={mAck.length + (mNew ? ` · ${mNew} new txns` : "")} />
          <Row label="Last flagged activity" value={mLast ? fmtDate(mLast) : "—"} />
          {mOpen.slice(0, 2).map(f => (
            <div key={f.id} style={{ fontSize: 12, color: f.confidence === "High" ? RED.fg : AMBER.fg, padding: "3px 0" }}>
              {f.kind === "NAME" ? f.mcaname : "Recurring debit"} — {f.txncount} txns</div>
          ))}
        </>}
      </Panel>

      <Panel title="Bank Activity" status={bankStatus}>
        {!d.canMCA ? <Note>Needs bank-feed access (portfolio managers and admins).</Note>
          : d.bankErr ? <Note>{d.bankErr}</Note> : <>
          <Row label="Open alerts" value={bOpen.length ? `${bOpen.length} (${bOpen.filter(a => a.severity === "High").length} high)` : "0"} />
          <Row label="Acknowledged" value={bAck.length} />
          {bOpen.slice(0, 3).map(a => (
            <div key={a.id} style={{ fontSize: 12, color: a.severity === "High" ? RED.fg : AMBER.fg, padding: "3px 0" }}>{a.title}</div>
          ))}
          {!bOpen.length && <Note>No open bank alerts.</Note>}
        </>}
      </Panel>
    </div>
  );
};

// ═════════════════════════════ MONITORING TAB ═════════════════════════════
// Every card on the tab looks like an Overview panel: a white card, the title
// INSIDE it in small cyan capitals over a thin light-cyan rule, with the status
// pill, the link to the full page and an optional action button on the same
// line. Cards sit 16px apart.
//
// OPEN ONLY WHEN THERE IS SOMETHING TO LOOK AT. A card with alerts (`alert`)
// opens; one with nothing waiting starts collapsed, its status pill saying so
// ("Clear", "In order"), so the eye goes to the cards that need it. Click the
// title to open or close any card; that holds until you leave the account. It
// is deliberately NOT remembered between visits: a card someone closed once
// must not stay closed after an alert turns up in it.
const Section = ({ title, status, link, onLink, action, alert, children }) => {
  const [chosen, setChosen] = useState(null);   // null = follow `alert`
  const open = chosen === null ? !!alert : chosen;
  const toggle = () => setChosen(!open);
  return (
    <Card style={{ padding: 16, marginBottom: 16 }}>
      <div style={{ ...cardTitle, marginBottom: open ? 12 : 0, paddingBottom: open ? 6 : 0,
                    borderBottom: open ? cardTitle.borderBottom : "none" }}>
        <span onClick={toggle} title={open ? "Collapse" : "Expand"}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
          <span style={{ display: "inline-block", fontSize: 8, color: "var(--color-text-secondary)",
                         transform: open ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}>▶</span>
          {title}
        </span>
        {status}
        {link && <LinkBtn onClick={onLink}>{link}</LinkBtn>}
        {/* The card's action button sits at the right edge, the same place and
            style as "+ Add Contact" on the Relationships tab. */}
        {action && <span style={{ marginLeft: link ? 12 : "auto", textTransform: "none", letterSpacing: 0, fontWeight: 400 }}>{action}</span>}
      </div>
      {open && children}
    </Card>
  );
};
const tbl = { width: "100%", borderCollapse: "collapse" };

// ── A table whose headers sort, same as the book-wide pages ──
// cols: [{ label, key?, numeric?, render(row) -> <Td/> }]. A column without a
// key (documents, say) is a plain header. Each table keeps its own sort.
const SortTable = ({ rows, cols, defaultKey, defaultDir, rowKey, rowStyle, onRowClick }) => {
  const { sorted, sortKey, sortDir, toggle } = useSortable(rows, defaultKey, defaultDir);
  return <table style={tbl}>
    <thead><tr>{cols.map(c => c.key
      ? <STh key={c.label} label={c.label} sortKey={c.key} currentKey={sortKey} currentDir={sortDir}
             onSort={toggle} numeric={c.numeric} />
      : (c.numeric ? <ThNum key={c.label}>{c.label}</ThNum> : <Th key={c.label}>{c.label}</Th>))}</tr></thead>
    <tbody>{sorted.map((r, k) => (
      <tr key={rowKey(r, k)} onClick={onRowClick ? () => onRowClick(r) : undefined}
          onMouseEnter={onRowClick ? e => { e.currentTarget.style.background = "var(--color-background-secondary)"; } : undefined}
          onMouseLeave={onRowClick ? e => { e.currentTarget.style.background = ""; } : undefined}
          style={{ ...(rowStyle ? rowStyle(r) : {}), ...(onRowClick ? { cursor: "pointer" } : {}) }}>
        {cols.map(c => <Fragment key={c.label}>{c.render(r)}</Fragment>)}
      </tr>))}</tbody>
  </table>;
};

export const MonitoringTab = ({ accountid }) => {
  const { go, api, role, setConfirm, delRec } = useApp();
  const [editing, setEditing] = useState(null); // a filing made outside CSC, being edited
  const d = useAccountMonitoring(accountid);
  const [detail, setDetail] = useState(null);   // a UCC filing opened from its row
  const [adding, setAdding] = useState(false);  // a filing made outside CSC
  const [search, setSearch] = useState(null);   // a UCC search opened from its row
  const [oldSearches, setOldSearches] = useState(false); // show superseded searches too
  if (d.loading) return <Note>Loading monitoring…</Note>;

  const ucc = d.ucc || [];
  const t = taxFacts(d.tax);

  // What each card has waiting — decides its pill and whether it starts open.
  const live = ucc.filter(isLive);
  const uccIssues = live.map(v => uccIssue(v)).filter(Boolean);
  const uccAlert = !!d.uccErr || uccIssues.length > 0 || live.length === 0;
  const uccStatus = d.uccErr ? null
    : uccIssues.some(i => i.tone === RED) ? <Pill tone={RED}>{uccIssues.length} need attention</Pill>
    : uccIssues.length ? <Pill tone={AMBER}>{uccIssues.length} need attention</Pill>
    : live.length ? <Pill tone={GREEN}>In order</Pill> : <Pill tone={AMBER}>No filing</Pill>;
  const curSearches = currentOnly(d.searches || []).filter(o => !o.notapplicable);
  const searchOpen = curSearches.filter(o => o.needsreview
    || (o.analysis && o.analysis.open > 0 && o.analysis.mode !== "prefunding")).length;
  const searchStatus = !curSearches.length ? null
    : searchOpen ? <Pill tone={AMBER}>{searchOpen} to look at</Pill> : <Pill tone={GREEN}>Clear</Pill>;
  const taxAlert = !!d.taxErr || t.atRisk.length > 0 || t.openWork.length > 0 || t.unmonitored.length > 0;
  const taxStatus = d.taxErr ? null
    : t.atRisk.length ? <Pill tone={RED}>Advances at risk</Pill>
    : t.openWork.length ? <Pill tone={AMBER}>{t.openWork.length} open</Pill>
    : t.unmonitored.length ? <Pill tone={AMBER}>Coverage gap</Pill>
    : <Pill tone={GREEN}>Clear</Pill>;
  const mOpen = (d.mca || []).filter(f => f.status === "Open");
  const mcaStatus = !d.canMCA || !d.mca ? null
    : mOpen.some(f => f.confidence === "High") ? <Pill tone={RED}>{mOpen.length} open</Pill>
    : mOpen.length ? <Pill tone={AMBER}>{mOpen.length} open</Pill> : <Pill tone={GREEN}>None open</Pill>;
  const bOpen = (d.bank || []).filter(a => a.status === "Open");
  const bankStatus = !d.canMCA || !d.bank ? null
    : bOpen.some(a => a.severity === "High") ? <Pill tone={RED}>{bOpen.length} open</Pill>
    : bOpen.length ? <Pill tone={AMBER}>{bOpen.length} open</Pill> : <Pill tone={GREEN}>Clear</Pill>;

  return <>
    {/* ── UCC ── */}
    <Section title="UCC Filings" status={uccStatus} alert={uccAlert} link="Open UCC register →" onLink={() => go("ucc-register")}
      // A filing another firm made directly (not through CSC), entered from its paperwork.
      action={role.canEdit && <Btn variant="primary" onClick={() => setAdding(true)}>+ Add Filing Made Outside CSC</Btn>}>
      {d.uccErr ? <Note>{d.uccErr}</Note> : !ucc.length ? <Note>No financing statement recorded for this borrower.</Note> :
        <div style={{ overflowX: "auto" }}>
          <SortTable rows={ucc} defaultKey="days_to_lapse" rowKey={v => v.uccfilingid}
            onRowClick={v => setDetail(v)} cols={[
            { label: "Debtor (as filed)", key: "debtorname_asfiled", render: v => <Td style={{ fontWeight: 500 }}>{v.debtorname_asfiled || "—"}</Td> },
            { label: "State", key: "jurisdiction_state", render: v => <Td>{v.jurisdiction_state || "—"}</Td> },
            { label: "File #", key: "filingnumber", render: v => <Td style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>{v.filingnumber || "—"}</Td> },
            // CSC's Order # (as on CSC's own screens); blank for a filing made outside CSC.
            { label: "CSC Order #", key: "csc_orderid", render: v => <Td style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>{v.csc_orderid ? String(v.csc_orderid).replace(/-\d+$/, "") : "—"}</Td> },
            { label: "Filed", key: "filedate", render: v => <Td>{fmtDate(v.filedate)}</Td> },
            { label: "Lapses", key: "lapsedate", render: v => <Td>{fmtDate(v.lapsedate)}</Td> },
            { label: "Days", key: "days_to_lapse", numeric: true, render: v => <TdNum>{v.days_to_lapse ?? "—"}</TdNum> },
            { label: "Status", key: "status", render: v => { const i = uccIssue(v);
                return <Td><Pill tone={uccTone(v)}>{i ? i.text : v.status}</Pill></Td>; } },
            { label: "Documents", render: v => <Td><FilingDocs api={api} v={v} compact /></Td> },
            { label: "Secured party", key: "securedparty_asfiled", render: v => <Td style={{ fontSize: 12, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                  title={v.securedparty_asfiled || ""}>{v.securedparty_asfiled || "—"}</Td> },
            // Same controls as every other record: view; and, for a filing made
            // outside CSC (entered here by hand), edit and — admins — delete.
            // A CSC filing is changed at CSC and arrives on the pull.
            { label: "", render: v => <Td><div style={{ display: "flex", gap: 6, justifyContent:"flex-end" }} onClick={e => e.stopPropagation()}>
                <Btn onClick={() => setDetail(v)}><Icon type="view" /></Btn>
                {role.canEdit && v.source !== "csc" && <Btn onClick={() => setEditing(v)}><Icon type="edit" /></Btn>}
                {role.canDelete && v.source !== "csc" && <Btn variant="danger" onClick={() => setConfirm({
                  msg: `Delete UCC filing ${v.filingnumber || ""} (${v.debtorname_asfiled || "no debtor"})? Its recorded history goes with it. This does not affect anything at a filing office.`,
                  onConfirm: () => delRec("UCCFilings", v.uccfilingid, "Deleted UCC filing " + (v.filingnumber || ""), () => d.reload()) })}>
                  <Icon type="delete" /></Btn>}
              </div></Td> },
          ]} />
        </div>}
    </Section>

    {/* ── UCC / lien searches ordered through CSC ── */}
    {/* Each search is checked: our filing still of record (nobody else terminated
        or amended it), and no active lien ahead of ours — or, before funding,
        the liens that had to go. Open a row for the report and the findings. */}
    <Section title="UCC & Lien Searches" status={searchStatus} alert={searchOpen > 0} link="Open UCC Searches →" onLink={() => go("ucc-searches")}>
      {!(d.searches && d.searches.length)
        ? <Note>No CSC searches tied to this borrower. Searches are matched from the name searched; tie one out on the UCC Searches page.</Note>
        : <div style={{ overflowX: "auto" }}>
            {/* The latest read search per state (and anything newer) — older ones are history. */}
            <SearchTable rows={oldSearches ? d.searches : currentOnly(d.searches)} onOpen={o => setSearch(o.csc_orderid)} />
            {d.searches.length > currentOnly(d.searches).length &&
              <a href="#" onClick={e => { e.preventDefault(); setOldSearches(x => !x); }}
                 style={{ display: "inline-block", marginTop: 6, fontSize: 12, color: B.cyan }}>
                {oldSearches ? "Hide older searches" : `Show ${d.searches.length - currentOnly(d.searches).length} older`}</a>}
          </div>}
    </Section>
    {search && (d.searches || []).some(o => o.csc_orderid === search) &&
      <SearchDetail o={d.searches.find(o => o.csc_orderid === search)} accounts={[]}
                    onClose={() => { setSearch(null); d.reload(); }} onChanged={() => d.reload()} />}

    {/* ── Tax ── */}
    <Section title="Tax Monitoring" status={taxStatus} alert={taxAlert} link="Open Tax Monitoring →" onLink={() => go("tax-monitoring")}>
      {d.taxErr ? <Note>{d.taxErr}</Note> : <>
        <div style={{ fontSize: 12, fontWeight: 600, color: B.cyanDark, margin: "2px 0 6px" }}>Liabilities</div>
        {!t.liabilities.length ? <Note>No tax liabilities recorded.</Note> :
          <div style={{ overflowX: "auto", marginBottom: 14 }}>
            <SortTable rows={t.liabilities} defaultKey="protection_ends" rowKey={(l, k) => l.taxliabilityid || k}
              rowStyle={l => ({ opacity: l.released ? 0.6 : 1 })} cols={[
              { label: "Entity", key: "borrowerguarantorname", render: l => <Td>{l.borrowerguarantorname || "—"}</Td> },
              { label: "Form", key: "form", render: l => <Td>{l.form || "—"}</Td> },
              { label: "Period", key: "period", render: l => <Td>{l.period || "—"}</Td> },
              { label: "Amount", key: "amount", numeric: true, render: l => <TdNum>{l.amount == null ? "—" : fmt(l.amount)}</TdNum> },
              { label: "Lien filed", key: "lien_filed_on", render: l => <Td>{l.lien_filed_on ? fmtDate(l.lien_filed_on) : "—"}</Td> },
              { label: "Protection ends", key: "protection_ends", render: l => <Td>{l.protection_ends ? `${fmtDate(l.protection_ends)}${l.days_of_protection_left != null ? ` (${l.days_of_protection_left}d)` : ""}` : "—"}</Td> },
              { label: "Status", key: "status_label", render: l => <Td><Pill tone={l.released ? GREY : l.advancing_at_risk ? RED : AMBER}>{l.status_label || (l.released ? "Released" : "Open")}</Pill></Td> },
            ]} />
          </div>}

        <div style={{ fontSize: 12, fontWeight: 600, color: B.cyanDark, margin: "2px 0 6px" }}>Coverage</div>
        {/* Entities marked "Not tracked" (exempt from tax monitoring) are left off this card. */}
        {!t.entities.length ? <Note>No borrower or guarantor entities on this account.</Note> :
          !t.entities.some(e => authState(e).key !== "exempt") ? <Note>Every entity on this account is marked not tracked.</Note> :
          <div style={{ overflowX: "auto" }}>
            <SortTable rows={t.entities.filter(e => authState(e).key !== "exempt").map(e => ({ ...e, _auth: authState(e) , _coverage: authState(e).label }))}
              defaultKey="borrowerguarantorname" rowKey={(e, k) => e.borrowerguarantorid || e.taxguardclientid || k} cols={[
              { label: "Entity", key: "borrowerguarantorname", render: e => <Td style={{ fontWeight: 500 }}>{e.borrowerguarantorname}</Td> },
              { label: "Role", key: "borrowerguarantor", render: e => <Td>{e.borrowerguarantor || "—"}</Td> },
              { label: "8821 through", key: "auth_to", render: e => <Td>{e.auth_to ? fmtDate(e.auth_to) : "—"}</Td> },
              { label: "Coverage", key: "_coverage", render: e => <Td><Pill tone={{ bg: e._auth.bg, fg: e._auth.fg }}>{e._auth.label}</Pill></Td> },
            ]} />
          </div>}
      </>}
    </Section>

    {/* ── MCA ── */}
    <Section title="MCA Activity" status={mcaStatus} alert={!!d.mcaErr || mOpen.length > 0} link={d.canMCA ? "Open MCA Monitor →" : null} onLink={() => go("mca-monitor")}>
      {!d.canMCA ? <Note>Bank activity is visible to portfolio managers and admins only.</Note>
        : <MCAMonitorPage accountid={accountid} />}
    </Section>

    {/* ── Bank activity ── */}
    <Section title="Bank Activity" status={bankStatus} alert={!!d.bankErr || bOpen.length > 0} link={d.canMCA ? "Open Bank Activity →" : null} onLink={() => go("bank-activity")}>
      {!d.canMCA ? <Note>Bank activity is visible to portfolio managers and admins only.</Note>
        : <BankActivityPage accountid={accountid} />}
    </Section>

    {/* ── News ── */}
    <AccountNews accountid={accountid} />

    {/* Same detail the UCC Filings page opens: history, CSC documents and
        history, recording events, correcting the borrower. */}
    {editing && <FilingForm accountid={accountid} rec={editing} onClose={() => setEditing(null)}
                            onSaved={() => { setEditing(null); d.reload(); }} />}
    {adding && <FilingForm accountid={accountid} onClose={() => setAdding(false)}
                           onSaved={() => { setAdding(false); d.reload(); }} />}
    {detail && <FilingDetail v={detail}
                             events={(d.uccEvents || []).filter(e => e.uccfilingid === detail.uccfilingid)}
                             onClose={() => setDetail(null)}
                             onSaved={() => { setDetail(null); d.reload(); }} />}
  </>;
};

// ── News: the News Monitor scoped to this borrower ──────────────────────────
// Who is searched for this account (company, borrowers, guarantors, contacts
// with Monitor news ticked), and every story found, any status. Same endpoints
// and the same decision / settings windows as the News Monitor page.
const SEV_TONE = { High: RED, Medium: AMBER, Low: GREY };
const STATUS_TONE = { Open: AMBER, Acknowledged: GREEN, "Not them": GREY };

const AccountNews = ({ accountid }) => {
  const { api, go, role } = useApp();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [decide, setDecide] = useState(null);
  const [edit, setEdit] = useState(null);
  const [showAll, setShowAll] = useState(false);   // decided stories too
  const [running, setRunning] = useState(null);
  const canEdit = !!role.canEdit;

  const load = () => {
    setErr(null);
    Promise.all([
      api.get("getNewsFindings", { accountid, status: "all" }),
      api.get("getNewsSubjects"),
    ]).then(([f, s]) => setD({
      findings: (f && f.findings) || [],
      subjects: ((s && s.subjects) || []).filter(x => x.accountid === accountid),
      duplicates: ((s && s.duplicates) || []).filter(x => x.accountid === accountid),
    })).catch(e => setErr(e.message));
  };
  useEffect(() => { load(); }, [accountid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Admins: search every name on this account now (one Serper credit each).
  const searchNow = async () => {
    const keys = d.subjects.filter(s => !s.exclude && !s.unsearchable).map(s => s.subjectkey);
    let n = 0, found = 0, errs = 0;
    for (const k of keys) {
      setRunning(`Searching ${++n} of ${keys.length}…`);
      try { const r = await api.post("runNewsScan", { subjectkey: k }); found += r.newfindings || 0; errs += r.errors || 0; }
      catch (e) { errs++; }
    }
    setRunning(`${keys.length} searched · ${found} new stor${found === 1 ? "y" : "ies"}${errs ? ` · ${errs} error${errs === 1 ? "" : "s"}` : ""}`);
    load();
  };

  if (err) return <Section title="News" alert><Note>{err}</Note></Section>;
  if (!d) return <Section title="News"><Note>Loading…</Note></Section>;

  const open = d.findings.filter(f => f.status === "Open");
  const openFlag = open.filter(f => f.severity !== "Low");   // Low = names them, no trouble word
  const rows = (showAll ? d.findings : open).map(f => ({ ...f, seen: f.seendate || f.createdat }));
  const status = open.some(f => f.severity === "High") ? <Pill tone={RED}>{openFlag.length} open</Pill>
    : openFlag.length ? <Pill tone={AMBER}>{openFlag.length} open</Pill>
    : open.length ? <Pill tone={GREY}>{open.length} low</Pill>
    : d.subjects.length ? <Pill tone={GREEN}>Nothing open</Pill> : null;
  const searchable = d.subjects.filter(s => !s.exclude && !s.unsearchable).length;
  const busy = !!running && running.startsWith("Searching");

  // Opens for open High / Medium stories; Low-only (no trouble word) stays closed.
  return <Section title="News" status={status} alert={openFlag.length > 0} link="Open News Monitor →" onLink={() => go("news-monitor")}
    action={role.isAdmin && searchable > 0 &&
      <Btn disabled={busy} onClick={searchNow}
           title="Search every name on this account now — one Serper credit each">{busy ? "Searching…" : "Search now"}</Btn>}>
    {running && <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 8 }}>{running}</div>}

    <div style={{ fontSize: 12, fontWeight: 600, color: B.cyanDark, margin: "2px 0 6px" }}>Names searched weekly</div>
    {!d.subjects.length ? <Note>Nothing is searched for this account — only funded accounts (Accrual / Non Accrual) are watched.</Note> :
      <div style={{ overflowX: "auto", marginBottom: 14 }}>
        <SortTable rows={d.subjects} defaultKey="kind" rowKey={s => s.subjectkey}
          rowStyle={s => ({ opacity: s.exclude ? 0.55 : 1 })} cols={[
          { label: "Name", key: "name", render: s => <Td style={{ fontWeight: 500 }}>{s.name}</Td> },
          { label: "Role", key: "kind", render: s => <Td>{s.kind}{s.isperson ? " · person" : ""}</Td> },
          { label: "Searched for", key: "query", render: s => <Td style={{ fontSize: 12 }}>
              {s.exclude ? <Pill tone={GREY}>not searched</Pill>
                : s.unsearchable ? <span style={{ color: AMBER.fg }}>{s.unsearchable}</span>
                : <span style={{ fontFamily: "var(--font-mono)", color: "var(--color-text-secondary)" }}>{s.query}</span>}</Td> },
          { label: "Last searched", key: "lastscanat", render: s => <Td style={{ fontSize: 12 }}>
              {s.lastscanat ? fmtDate(s.lastscanat) : "not yet"}
              {s.laststatus === "error" && <div style={{ color: RED.fg }} title={s.lasterror}>{String(s.lasterror || "").slice(0, 70)}</div>}</Td> },
          { label: "Open", key: "openfindings", numeric: true, render: s => <TdNum>{s.openfindings || "—"}</TdNum> },
          { label: "", render: s => <Td style={{ textAlign: "right" }}>{canEdit && <Btn onClick={() => setEdit(s)}>Settings</Btn>}</Td> },
        ]} />
        {d.duplicates.length > 0 && <Note>{d.duplicates.length} more name{d.duplicates.length === 1 ? " is" : "s are"} the same as one above and searched once
          ({d.duplicates.map(x => x.name).join(", ")}).</Note>}
        <Note>To add a contact, open it on the Relationships tab and tick <b>Monitor news</b>.</Note>
      </div>}

    <div style={{ fontSize: 12, fontWeight: 600, color: B.cyanDark, margin: "2px 0 6px", display: "flex", alignItems: "center" }}>
      {showAll ? "All stories" : "Open stories"}
      {d.findings.length > open.length && <a href="#" onClick={e => { e.preventDefault(); setShowAll(x => !x); }}
          style={{ marginLeft: "auto", fontSize: 12, fontWeight: 400, color: B.cyan }}>
        {showAll ? "Open only" : `Show ${d.findings.length - open.length} decided`}</a>}
    </div>
    {!rows.length ? <Note>{showAll ? "No stories found for this account." : "No open stories."}</Note> :
      <div style={{ overflowX: "auto" }}>
        <SortTable rows={rows} defaultKey="seen" defaultDir="desc" rowKey={f => f.id} cols={[
          { label: "Date", key: "seen", render: f => <Td style={{ whiteSpace: "nowrap" }}>{fmtDate(f.seen)}</Td> },
          { label: "Name", key: "subjectname", render: f => <Td>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--color-text-secondary)" }}>{f.subjectkind}</div>
              {f.subjectname}</Td> },
          { label: "Story", key: "title", render: f => <Td style={{ minWidth: 300 }}>
              <a href={f.url} target="_blank" rel="noopener noreferrer"
                 style={{ color: "var(--color-text-primary)", fontWeight: 500, textDecoration: "none" }}>{f.title} ↗</a>
              <div style={{ fontSize: 11.5, color: "var(--color-text-secondary)", marginTop: 2 }}>
                {f.source || f.domain}{f.alsoseen > 0 ? ` · also on ${f.alsoseen} other site${f.alsoseen === 1 ? "" : "s"}` : ""}
                {f.matchedterms ? ` · ${f.matchedterms}` : ""}</div>
              {f.snippet && <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 3 }}>{f.snippet}</div>}
              {f.note && <div style={{ fontSize: 12, marginTop: 4, whiteSpace: "pre-wrap" }}>
                <b>{f.status}</b> by {f.decidedby}{f.decidedat ? " · " + fmtDate(f.decidedat) : ""}: {f.note}</div>}</Td> },
          { label: "Severity", key: "severity", render: f => <Td><Pill tone={SEV_TONE[f.severity] || AMBER}>{f.severity}</Pill></Td> },
          { label: "Status", key: "status", render: f => <Td><Pill tone={STATUS_TONE[f.status] || AMBER}>{f.status}</Pill></Td> },
          { label: "", render: f => <Td style={{ whiteSpace: "nowrap", textAlign: "right" }}>
              {canEdit && f.status === "Open" && <span style={{ display: "inline-flex", gap: 6 }}>
                <Btn onClick={() => setDecide({ ids: [f.id], status: "Acknowledged" })}>Acknowledge</Btn>
                <Btn onClick={() => setDecide({ ids: [f.id], status: "Not them" })}>Not them</Btn></span>}
              {canEdit && f.status !== "Open" && <Btn onClick={() => setDecide({ ids: [f.id], status: "Open" })}>Reopen</Btn>}</Td> },
        ]} />
      </div>}

    {decide && <DecisionModal decide={decide} onClose={() => setDecide(null)} onSaved={() => { setDecide(null); load(); }} />}
    {edit && <SubjectModal s={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
  </Section>;
};
